import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight, ChevronUp, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { LocalClock } from "./LocalClock";
import { Logo } from "./Logo";
import { links } from "../data/portfolio";
import { lockScroll } from "../utils/scrollLock";
const navItems = [{
  label: "Work",
  id: "work",
  href: "/#work"
}, {
  label: "About",
  id: "about",
  href: "/#about"
}, {
  label: "Services",
  id: "services",
  href: "/#services"
}, {
  label: "Reviews",
  id: "testimonials",
  href: "/#testimonials"
}, {
  label: "FAQ",
  id: "faq",
  href: "/#faq"
}];
export function SiteNavigation() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const location = useLocation();
  const sheetRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const {
    scrollYProgress
  } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 30,
    mass: 0.4
  });
  useEffect(() => {
    setOpen(false);
    const sections = navItems.map(item => document.getElementById(item.id)).filter(Boolean) as HTMLElement[];
    if (!sections.length) return;
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActive(visible.target.id);
    }, {
      rootMargin: "-45% 0px -45% 0px",
      threshold: [0.01, 0.25, 0.5]
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [location]);
  useEffect(() => {
    if (!open) return;
    const releaseScroll = lockScroll();
    sheetRef.current?.querySelector<HTMLElement>("a")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      releaseScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);
  return <header className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center px-4 pb-5 md:pb-7" data-id="element-412" data-mp="30b2clnjozne5">
      <AnimatePresence data-id="element-413" data-mp="3499reculiemp">
        {open && <motion.div id="nav-sheet" ref={sheetRef} className="pointer-events-auto mb-3 w-full max-w-[420px] overflow-hidden rounded-[26px] border border-white/10 bg-[#070b0b]/95 p-4 backdrop-blur-xl lg:hidden" initial={{
        opacity: 0,
        y: 18,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 18,
        scale: 0.97
      }} transition={{
        duration: 0.34,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-414" data-mp="3s0f2yfti6kr7">
            <div className="flex flex-col" data-id="element-415" data-mp="2ckpszff3427q">
              {[...navItems, {
            label: "Portfolio",
            id: "portfolio",
            href: "/videos"
          }].map((item, index) => <a key={item.label} href={item.href} aria-current={active === item.id ? "true" : undefined} onClick={() => setOpen(false)} className="flex min-h-[54px] items-center justify-between border-b border-white/10 px-2 text-base text-white last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-416" data-mp="2458yh1ryqu3o" data-mpi={index}>
                  <span data-id="element-417" data-mp="1gecz9jl6gwme">{item.label}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/45" data-id="element-418" data-mp="1gfem0lbk37jy">{String(index + 1).padStart(2, "0")}</span>
                </a>)}
              <div className="mt-4 flex items-center justify-between px-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-419" data-mp="1qazkwalbvrph">
                <LocalClock data-id="element-420" data-mp="12ju6lzv1d7ha" />
                <span data-id="element-421" data-mp="xnlr2lcxfc9l">+973 398 64230</span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      <motion.nav aria-label="Primary" className="pointer-events-auto relative flex max-w-[calc(100vw-2rem)] items-center gap-1 overflow-hidden rounded-2xl border border-white/10 bg-[#070b0b]/85 p-2 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.9)] backdrop-blur-xl" initial={{
      y: 30,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.75,
      delay: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-422" data-mp="x9zvwbtdce2">
        <Link to="/" data-cursor="Home" aria-label="Home, SM Zeeshan" className="grid h-11 shrink-0 place-items-center rounded-xl bg-white/[0.08] px-4 transition-colors hover:bg-white/[0.16] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-423" data-mp="olb08hyu9mie">
          <Logo className="h-[22px] w-[48px]" data-id="element-424" data-mp="2vc977qb9wive" />
        </Link>

        <div className="hidden items-center lg:flex" data-id="element-425" data-mp="2ve3psxz2nywb">
          {navItems.map(item => <a key={item.label} href={item.href} data-cursor="Go" aria-current={active === item.id ? "true" : undefined} className={`relative flex h-11 items-center whitespace-nowrap rounded-xl px-3.5 text-[13px] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] xl:px-5 xl:text-sm ${active === item.id ? "text-white" : "text-white/60 hover:text-white"}`} data-id="element-426" data-mp="2zff9kmv9lpwl">
              {active === item.id && <motion.span layoutId="nav-active" className="absolute inset-0 rounded-xl bg-white/[0.08]" transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-427" data-mp="35t315nwlcp25" />}
              <span className="relative" data-id="element-428" data-mp="2wsmu64gn2zsj">{item.label}</span>
            </a>)}
          <Link to="/videos" data-cursor="Go" className="flex h-11 items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 text-[13px] text-white/60 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] xl:px-5 xl:text-sm" data-id="element-429" data-mp="1qef8vatp1r9d">
            Portfolio <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-430" data-mp="1du8g51ofysh8" />
          </Link>
        </div>

        <button ref={toggleRef} type="button" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="nav-sheet" onClick={() => setOpen(!open)} className="flex h-11 items-center gap-2 rounded-xl px-4 text-sm text-white/75 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] lg:hidden" data-id="element-431" data-mp="1hvjzwqkmwjhi">
          {open ? <X className="h-4 w-4" aria-hidden="true" data-id="element-432" data-mp="z5ltsg0ed0gi" /> : <ChevronUp className="h-4 w-4" aria-hidden="true" data-id="element-433" data-mp="l5t2ojkyki80" />}
          Menu
        </button>

        <a href={links.calendly} target="_blank" rel="noreferrer" data-cursor="Book" className="group relative flex h-11 shrink-0 items-center gap-2 overflow-hidden rounded-xl bg-[#54f6c6] px-4 text-[13px] font-semibold text-[#05100d] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white xl:px-5 xl:text-sm" data-id="element-434" data-mp="q4e6eszf5k17">
          <span aria-hidden="true" className="absolute inset-0 -translate-x-full bg-white/70 transition-transform duration-500 group-hover:translate-x-0" data-id="element-435" data-mp="bhfjnbbibdst" />
          <span className="relative whitespace-nowrap" data-id="element-436" data-mp="pfnad1hy9ivj">Book a call</span>
          <ArrowUpRight className="relative h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-437" data-mp="khu7ahby14zh" />
        </a>

        <motion.span aria-hidden="true" className="absolute inset-x-6 bottom-0 h-px origin-left bg-[#54f6c6]/60" style={{
        scaleX: progress
      }} data-id="element-438" data-mp="u86mrv4wi7yr" />
      </motion.nav>
    </header>;
}